import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Crown, MapPin, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ContactButtons } from "@/lib/coffee-contact";
import { formatPlace } from "@/lib/districts";
import { GROUP_STATUS_LABEL, canReportOutcome, slotLabel } from "@/lib/group-coffee";

export const Route = createFileRoute("/_authenticated/coffee_/group/$groupId")({
  head: () => ({
    meta: [
      { title: "Кофе на районе — TurkeyDrive" },
      { name: "description", content: "Твоя кофе-компания на этой неделе: люди, место и время встречи." },
      { property: "og:title", content: "Кофе на районе — TurkeyDrive" },
      { property: "og:description", content: "Твоя кофе-компания на этой неделе: люди, место и время встречи." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: GroupPage,
});

function initials(name: string) {
  return (name || "?").split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

function GroupPage() {
  const { groupId } = Route.useParams();
  const { user } = useAuth();
  const qc = useQueryClient();

  const groupQ = useQuery({
    queryKey: ["coffee-group", groupId],
    queryFn: async () => {
      const { data, error } = await supabase.from("coffee_groups").select("*").eq("id", groupId).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const peopleQ = useQuery({
    queryKey: ["coffee-group", groupId, "people"],
    enabled: !!groupQ.data,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("coffee_group_people", { p_group_id: groupId });
      if (error) throw error;
      return data ?? [];
    },
  });

  const g = groupQ.data;
  const people = peopleQ.data ?? [];
  const me = people.find((p) => p.user_id === user?.id);
  const isOrganizer = !!g && g.organizer_user_id === user?.id;
  const joined = people.filter((p) => p.status === "joined");
  const myInterests = new Set((me?.interests ?? []).map((s) => s.toLowerCase()));

  const [place, setPlace] = useState("");
  const [meetAt, setMeetAt] = useState("");
  useEffect(() => { setPlace(g?.place ?? ""); setMeetAt(g?.meet_at ?? ""); }, [g?.place, g?.meet_at]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["coffee-group", groupId] });
    qc.invalidateQueries({ queryKey: ["coffee-group-current"] });
  };
  const respond = useMutation({
    mutationFn: async (status: "joined" | "declined") => {
      const { error } = await supabase.from("coffee_group_members").update({ status }).eq("group_id", groupId).eq("user_id", user!.id);
      if (error) throw error;
      return status;
    },
    onSuccess: (s) => { toast.success(s === "joined" ? "Отлично, ты в деле!" : "Хорошо, в другой раз"); refresh(); },
    onError: () => toast.error("Не удалось сохранить — попробуй ещё раз"),
  });
  const outcome = useMutation({
    mutationFn: async (o: "met" | "missed") => {
      const { error } = await supabase.from("coffee_group_members").update({ outcome: o }).eq("group_id", groupId).eq("user_id", user!.id);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Спасибо, записали"); refresh(); },
    onError: () => toast.error("Не удалось сохранить"),
  });
  const saveMeeting = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("coffee_groups").update({ place: place.trim() || null, meet_at: meetAt.trim() || null }).eq("id", groupId);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Сохранено — участники увидят"); refresh(); },
    onError: () => toast.error("Не удалось сохранить"),
  });
  const transfer = useMutation({
    mutationFn: async (uid: string) => {
      const { error } = await supabase.rpc("transfer_coffee_group_organizer", { p_group_id: groupId, p_user_id: uid });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Организатор передан"); refresh(); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (groupQ.isLoading) return <main className="mx-auto max-w-2xl px-4 py-8 text-sm text-td-muted">Загружаем…</main>;
  if (!g) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-12 text-center">
        <h1 className="text-xl font-semibold text-td-dark">Группа недоступна</h1>
        <p className="mt-2 text-sm text-td-muted">Её видят только участники.</p>
        <Link to="/coffee" className="mt-4 inline-block text-sm font-semibold text-td-teal underline">В раздел «Кофе»</Link>
      </main>
    );
  }

  const active = g.status === "forming" || g.status === "confirmed";

  return (
    <main className="mx-auto min-w-0 max-w-2xl px-4 py-6">
      <Link to="/coffee" className="text-sm text-td-muted hover:text-td-dark">← Кофе</Link>
      <h1 className="mt-2 font-display text-2xl font-bold text-td-dark">Кофе на районе</h1>
      <p className="mt-1 text-sm text-td-muted">
        {formatPlace(g.city, g.district)} · {GROUP_STATUS_LABEL[g.status] ?? g.status}
        {g.suggested_slot ? ` · удобно: ${slotLabel(g.suggested_slot).toLowerCase()}` : ""}
      </p>

      <section className="mt-5 rounded-2xl border border-td-line bg-white p-4">
        <div className="flex items-start gap-2 text-sm text-td-dark"><MapPin className="mt-0.5 size-4 shrink-0 text-td-teal" /><span className="min-w-0 break-words">{g.place || "Место пока не выбрано"}</span></div>
        <div className="mt-2 flex items-start gap-2 text-sm text-td-dark"><Clock className="mt-0.5 size-4 shrink-0 text-td-teal" /><span className="min-w-0 break-words">{g.meet_at || "Время пока не выбрано"}</span></div>
        {isOrganizer && active && (
          <div className="mt-4 space-y-3 border-t border-td-line pt-4">
            <p className="text-xs text-td-muted">Ты организатор — предложи место и время</p>
            <div><Label htmlFor="g-place">Место</Label><Input id="g-place" className="mt-1" placeholder="Например: кофейня у парка" value={place} onChange={(e) => setPlace(e.target.value)} /></div>
            <div><Label htmlFor="g-when">Когда</Label><Input id="g-when" className="mt-1" placeholder="Например: суббота, 11:00" value={meetAt} onChange={(e) => setMeetAt(e.target.value)} /></div>
            <Button className="h-11 w-full bg-td-teal hover:bg-td-teal/90" disabled={saveMeeting.isPending} onClick={() => saveMeeting.mutate()}>{saveMeeting.isPending ? "Сохраняем…" : "Сохранить"}</Button>
          </div>
        )}
      </section>

      {me && active && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button className={`h-11 ${me.status === "joined" ? "bg-td-teal hover:bg-td-teal/90" : "bg-td-orange hover:bg-td-orange/90"}`} disabled={respond.isPending || me.status === "joined"} onClick={() => respond.mutate("joined")}>
            {me.status === "joined" ? "Ты в деле ✓" : "Я в деле"}
          </Button>
          <Button variant="outline" className="h-11" disabled={respond.isPending || me.status === "declined"} onClick={() => respond.mutate("declined")}>
            {me.status === "declined" ? "Пропускаю" : "В этот раз пропущу"}
          </Button>
        </div>
      )}
      {me && me.status !== "joined" && active && <p className="mt-2 text-xs text-td-muted">Контакты откроются, когда нажмёшь «Я в деле»</p>}

      {me && canReportOutcome(g.created_at) && me.status === "joined" && (
        <div className="mt-4 rounded-2xl border border-td-line bg-td-soft/40 p-4">
          <p className="text-sm font-semibold text-td-dark">Как прошла встреча?</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Button className="h-11 bg-td-teal hover:bg-td-teal/90" disabled={outcome.isPending || me.outcome === "met"} onClick={() => outcome.mutate("met")}>Встретились</Button>
            <Button variant="outline" className="h-11" disabled={outcome.isPending || me.outcome === "missed"} onClick={() => outcome.mutate("missed")}>Не получилось</Button>
          </div>
        </div>
      )}

      <h2 className="mt-6 text-sm font-semibold text-td-dark">Кто в компании · подтвердили {joined.length} из {people.length}</h2>
      <ul className="mt-3 space-y-3">
        {people.map((p) => {
          const common = p.user_id === user?.id ? [] : (p.interests ?? []).filter((i) => myInterests.has(i.toLowerCase()));
          const showContacts = p.user_id !== user?.id && (!!p.telegram || (p.social_links ?? []).length > 0);
          return (
            <li key={p.user_id} className="rounded-2xl border border-td-line bg-white p-3">
              <div className="flex items-center gap-3">
                <Link to="/people/$userId" params={{ userId: p.user_id }} className="shrink-0">
                  {p.photo_url ? <img loading="lazy" decoding="async" src={p.photo_url} alt={p.full_name} className="size-12 rounded-full object-cover" /> : <span className="flex size-12 items-center justify-center rounded-full bg-td-soft text-sm font-semibold text-td-dark">{initials(p.full_name)}</span>}
                </Link>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-td-dark">
                    <span className="truncate">{p.full_name}{p.user_id === user?.id ? " (ты)" : ""}</span>
                    {g.organizer_user_id === p.user_id && <Crown className="size-3.5 shrink-0 text-td-orange" aria-label="Организатор" />}
                  </p>
                  <p className="truncate text-xs text-td-muted">{formatPlace(p.city, p.district, p.neighborhood)}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${p.status === "joined" ? "bg-td-teal/10 text-td-teal" : p.status === "declined" ? "bg-td-soft text-td-muted" : "bg-td-orange/10 text-td-orange"}`}>
                  {p.status === "joined" ? "В деле" : p.status === "declined" ? "Пропускает" : "Думает"}
                </span>
              </div>
              {common.length > 0 && <p className="mt-2 text-xs text-td-dark/80">Общее: {common.slice(0, 4).join(", ")}</p>}
              {showContacts && <div className="mt-2"><ContactButtons telegram={p.telegram} links={p.social_links ?? []} /></div>}
              {isOrganizer && active && p.status === "joined" && p.user_id !== user?.id && (
                <button type="button" className="mt-2 text-xs font-medium text-td-teal underline" onClick={() => transfer.mutate(p.user_id)}>Сделать организатором</button>
              )}
            </li>
          );
        })}
      </ul>
    </main>
  );
}
